import { useEffect, useState, useMemo } from "react";

import { inject } from "../../../../../lib/itn-ui-kit/services/service-container";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import { createActiveChipLabel } from "../utils/functions";
import { MultiSelectDB, SingleSelectDB } from "../utils/store";

import { useFetchFilterOpts } from "./useFetchFilterOpts";

export const useCaseListFilters = (filterListConfig) => {
  const currentUserService = inject("current-user-service");
  const { getOptions } = useFetchFilterOpts();
  const { filters } = useQueryParams();
  const [filterState, setFilterState] = useState([]);

  const allowedFilters = useMemo(() => {
    return filterListConfig.filter((filter) => {
      if (filter?.permission?.length > 0) {
        return currentUserService?.userRoles?.permissions.includes(filter.permission);
      }
      return true;
    });
  }, [currentUserService?.userRoles?.permissions]);

  const activeFilters = useMemo(
    () => filterState.filter((filter) => filter.activeChip.isActive),
    [filterState]
  );

  async function initializeFilterState() {
    const updatedFilterState = [];

    for (const filter of allowedFilters) {
      const updatedFilter = {
        ...filter,
        currentValue: null,
        activeChip: {
          ...filter.activeChip,
          isActive: false,
          label: "",
        },
      };

      switch (filter.filterType) {
        case "MultiSelectSearch": {
          const filterValue =
            filters.get(filter.fieldName) || filters.get(filter.fieldName + "__in");
          if (filterValue) {
            let currentOpts = null;
            const parsedFilterValue = filterValue
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v.length > 0);
            updatedFilter.currentValue = parsedFilterValue;

            try {
              const storedOpts = await MultiSelectDB.get(filter.fieldName);
              const allOptionsAreStored = parsedFilterValue.every((value) =>
                (storedOpts || []).some((opt) => String(opt[filter.valueKey]) === String(value))
              );

              if (!allOptionsAreStored) {
                const fetchedOpts = await getOptions({
                  apiUrl: filter.apiUrl,
                  valueKey: filter.valueKey,
                  selectedItems: parsedFilterValue,
                  limit: 1000, // To avoid too many requests
                });

                if (fetchedOpts && fetchedOpts.length > 0) {
                  const filteredFetchedOpts = fetchedOpts.filter((opt) =>
                    parsedFilterValue.includes(String(opt[filter.valueKey]))
                  );
                  currentOpts = filteredFetchedOpts.map((opt) => opt[filter.nameKey]);
                  // Save newly fetched options to IndexedDB
                  // Avoid multiple fetches by merging with existing stored options
                  await MultiSelectDB.save(filter.fieldName, fetchedOpts);
                }
              } else {
                currentOpts = storedOpts
                  .filter((opt) => parsedFilterValue.includes(String(opt[filter.valueKey])))
                  .map((opt) => opt[filter.nameKey]);
              }
            } catch (error) {
              console.error(`Error loading initial options for ${filter.fieldName}:`, error);
            }

            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: parsedFilterValue.length > 0,
              label: createActiveChipLabel(currentOpts ?? parsedFilterValue, filter.filterType),
            };
          }
          break;
        }

        case "SingleSelect": {
          const filterValue = filters.get(filter.fieldName);
          if (filterValue) {
            let existingValues = null;
            updatedFilter.currentValue = filterValue;

            try {
              const storedOpt = await SingleSelectDB.get(filter.fieldName);

              if (storedOpt) {
                existingValues = [storedOpt[filter.nameKey]];
              } else {
                const opts = await getOptions({
                  apiUrl: filter.apiUrl,
                  valueKey: filter.valueKey,
                  selectedItems: [filterValue],
                });

                if (opts && opts.length > 0) {
                  const foundInNew = opts.find(
                    (opt) => String(opt[filter.valueKey]) === String(filterValue)
                  );
                  if (foundInNew) {
                    existingValues = [foundInNew[filter.nameKey]];
                    await SingleSelectDB.save(filter.fieldName, foundInNew);
                  }
                }
              }
            } catch (error) {
              console.error(`Error loading initial options for ${filter.fieldName}:`, error);
            }

            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: true,
              label: createActiveChipLabel(existingValues?.[0] ?? filterValue, filter.filterType),
            };
          }
          break;
        }

        case "DateRange":
        case "RangeSlider": {
          const gteValue = filters.get(filter.fieldName + "__gte");
          const lteValue = filters.get(filter.fieldName + "__lte");

          if (gteValue || lteValue) {
            updatedFilter.currentValue = {
              __gte: gteValue || undefined,
              __lte: lteValue || undefined,
            };
            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: !!(gteValue || lteValue),
              label: createActiveChipLabel({ __gte: gteValue, __lte: lteValue }, filter.filterType),
            };
          }
          break;
        }

        case "SearchOptionGroup":
        case "DatePicker":
        case "Search": {
          const filterValue = filters.get(filter.fieldName);
          if (filterValue) {
            updatedFilter.currentValue = filterValue;
            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: true,
              label: createActiveChipLabel(
                filterValue,
                filter.filterType,
                filter.customTrueLabel ?? "Yes",
                filter.customFalseLabel ?? "No"
              ),
            };
          }
          break;
        }

        default: {
          // Handle any other filter types
          const filterValue = filters.get(filter.fieldName);
          if (filterValue) {
            let parsedValue;
            try {
              parsedValue = JSON.parse(filterValue);
            } catch (e) {
              parsedValue = filterValue;
            }
            updatedFilter.currentValue = parsedValue;
            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: true,
              label: createActiveChipLabel(parsedValue, filter.filterType),
            };
          }
          break;
        }
      }

      updatedFilterState.push(updatedFilter);
    }

    setFilterState(updatedFilterState);
  }

  // Initialize local filter state from URL query params
  useEffect(() => {
    initializeFilterState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedFilters, filters]);

  return {
    activeFilters,
    filterState,
  };
};
